import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { UserCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog d'assignation/réassignation d'un commercial à un client.
 * Raison obligatoire (audit OHADA).
 */
export function AssignCommercialDialog({
  open, onOpenChange, client, onSuccess,
}) {
  const [commercials, setCommercials] = useState([]);
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(client?.assignedCommercialId || 'NONE');
    setReason('');
    axios.get(`${API}/admin/commercials`)
      .then((r) => setCommercials(r.data?.commercials || []))
      .catch(() => toast.error('Impossible de charger la liste des commerciaux'));
  }, [open, client]);

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error('Raison obligatoire (audit OHADA)');
      return;
    }
    setSaving(true);
    try {
      const isBulk = client?.id === '__BULK__';
      if (isBulk) {
        const ids = window.__bulkClientIds__ || [];
        if (ids.length === 0) {
          toast.error('Aucun client sélectionné');
          setSaving(false);
          return;
        }
        const body = {
          clientIds: ids,
          commercialId: selected === 'NONE' ? null : selected,
          reason: reason.trim(),
        };
        const { data } = await axios.post(`${API}/admin/companies/bulk-assign-commercial`, body);
        toast.success(`${data.updated} entreprise(s) assignée(s)`, { description: data.skipped > 0 ? `${data.skipped} ignorée(s)` : undefined });
      } else {
        const body = {
          commercialId: selected === 'NONE' ? null : selected,
          reason: reason.trim(),
        };
        await axios.patch(`${API}/admin/companies/${client.id}/commercial`, body);
        toast.success(selected === 'NONE' ? 'Commercial désassigné' : 'Commercial assigné');
      }
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error('Erreur', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-600" />
            Assigner un commercial
          </DialogTitle>
          <DialogDescription>
            Entreprise : <strong>{client.companyName}</strong>
            <br />
            Actuellement : {client.assignedCommercialName ? <span className="text-emerald-700 font-medium">{client.assignedCommercialName}</span> : <span className="italic text-slate-500">Aucun commercial assigné</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Commercial Baobab Connect</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="mt-1 h-9" data-testid="commercial-select">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE"><em>Aucun (désassigner)</em></SelectItem>
                {commercials.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.fullName} <span className="text-[10px] text-slate-500 ml-1">({c.role === 'lead_commercial' ? 'Lead' : 'Com.'})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-medium">
              Raison de l&apos;assignation <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Nouveau client onboardé / Transfert de portefeuille / Spécialité secteur..."
              className="mt-1 min-h-[70px] text-sm"
              data-testid="commercial-reason"
            />
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-800">
            <strong>Portée</strong> : ce commercial sera appliqué à <strong>tous les utilisateurs</strong> de l&apos;entreprise
            « {client.companyName} » et à <strong>toutes ses opérations</strong> (passées et futures).
            Chaque cascade est tracée dans l&apos;audit log OHADA (horodatage, acteur, raison).
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving || reason.trim().length < 3}
            title={reason.trim().length < 3 ? 'Raison obligatoire (min. 3 caractères)' : undefined}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="commercial-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Valider l&apos;assignation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssignCommercialDialog;
